import { PlatformTypeEnum } from '@/common/constants';
import { ContractTypesEnum } from '@/common/constants/contract.types.constants';
import { PartiesEnum } from '@/common/constants/parties.constants';
import SelectControl from '@/components/controls/select-control';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { usePartyStore } from '@/features/parties/store';
import useStore from '@/lib/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ContractType, FormManagaValues } from '../../models/contract';
import { useLoadStore } from '../../store';
import { contractSchema } from '../general/schemas/contract.schema';
import useMeasureStore from '../general/store/useMeasureStore';

export const EditContract = () => {
  const [dealType, setDealType] = useState<ContractType | undefined>(undefined);
  const form = useForm<FormManagaValues>({
    defaultValues: {
      farmer_contract_id: '',
      crop_generation_type: '',
      crop_generation_id: '',
      storage_type: '',
    },
    context: { dealType },
    resolver: yupResolver(contractSchema),
  });
  const { user } = useStore();
  const { t } = useTranslation();
  const {
    contractList: contracts,
    manageContract,
    manageGrainContract,
    isManageContractLoading,
    isManageGrainContractLoading,
    fetchFarmerContracts,
  } = useMeasureStore();
  const {
    load,
    loadGrain,
    fetchDataDetail,
    fetchFarmerGrain,
    fetchGrainDataDetail,
    grainContracts,
  } = useLoadStore();
  const { generationTypes, cropGenerations, storageTypes } = usePartyStore();
  const [contractList, setContractList] = useState<ContractType[] | any>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const isCotton = user?.platform_type === PlatformTypeEnum.COTTON;

  const farmerContractId = useWatch({
    control: form.control,
    name: 'farmer_contract_id',
  });

  const crop_generation_type = useWatch({
    control: form.control,
    name: 'crop_generation_type',
  });

  useEffect(() => {
    if (openDialog && loadGrain?.farmer_tax_number) {
      fetchFarmerGrain(+loadGrain?.farmer_tax_number);
    }
    if (openDialog && load?.id) {
      fetchFarmerContracts(load?.id);
    }
  }, [openDialog, load?.id, loadGrain?.farmer_tax_number]);

  useEffect(() => {
    setContractList(
      contracts
        ?.filter((v) => v?.id !== load?.farmer_contract_id)
        ?.map((el) => ({
          label:
            el?.deal_number == null
              ? `${el?.contract_number} / "${t(el.deal_type)}" - ${el.buyer_name}`
              : `${el?.deal_number} / "${t(el.deal_type)}" - ${el.buyer_name}`,
          value: el.id,
          ...el,
        })),
    );
  }, [contracts]);

  const onSubmit: SubmitHandler<FormManagaValues> = async (data) => {
    try {
      const payload = isCotton
        ? {
            farmer_contract_id: data.farmer_contract_id,
            crop_generation_type: data.crop_generation_type,
            ...(data.crop_generation_type !== 'TECHNICAL' && {
              crop_generation_id: data.crop_generation_id,
              storage_type: data.storage_type,
            }),
          }
        : {
            farmer_contract_id: data.farmer_contract_id,
          };

      if (isCotton) {
        await manageContract(payload, load?.id!);
        await fetchDataDetail(load?.id!);
      } else {
        await manageGrainContract(payload, loadGrain?.id!);
        await fetchGrainDataDetail(loadGrain?.id!);
      }
      setOpenDialog(false);
      toast.success(t('data.saved'));
    } catch (e) {
      console.error(e);
    }
  };

  const contract = useMemo(() => {
    setDealType(contractList.find((el: any) => el.id === farmerContractId));
    return contractList?.find((el: any) => el.id === farmerContractId);
  }, [farmerContractId]);

  const filteredGenerationTypes = useMemo(() => {
    if (!contract || !generationTypes) return [];
    if (contract.deal_type !== ContractTypesEnum.STORAGE) {
      return contract.is_seed
        ? generationTypes.filter((v) => v.value !== 'TECHNICAL')
        : generationTypes.filter((v) => v.value !== 'SEED');
    }
    return generationTypes;
  }, [contract, generationTypes]);

  useEffect(() => {
    if (!contract || !generationTypes) return;

    if (contract.deal_type !== ContractTypesEnum.STORAGE) {
      if (contract.is_seed) {
        form.setValue(
          'crop_generation_type',
          String(
            generationTypes.find((v) => v.value !== 'TECHNICAL')?.value ?? '',
          ),
        );
      } else {
        form.setValue(
          'crop_generation_type',
          String(generationTypes.find((v) => v.value !== 'SEED')?.value ?? ''),
        );
      }
    }
  }, [contract, generationTypes]);

  return (
    <Form {...form}>
      <Dialog onOpenChange={setOpenDialog} open={openDialog}>
        <DialogTrigger asChild>
          <Button
            size="icon"
            className="border-grey-500 w-5 bg-transparent"
            variant="ghost"
          >
            <EditIcon className="w-5" color="green" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[625px]">
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{t('edit.contract')}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3 my-5">
              <SelectControl
                name="farmer_contract_id"
                label={t('contract')}
                options={
                  isCotton
                    ? contractList
                    : grainContracts?.map((el) => ({
                        label: el?.contract_number,
                        value: el?.id,
                      })) || []
                }
                control={form.control}
                placeholder={t('choose.data')}
                disabledItem={false}
              />
              {isCotton && (
                <SelectControl
                  name="crop_generation_type"
                  label={t('generation.type')}
                  options={filteredGenerationTypes}
                  control={form.control}
                  placeholder={t('choose.data')}
                  disabled={contract?.deal_type !== ContractTypesEnum.STORAGE}
                />
              )}
              {crop_generation_type === PartiesEnum.SEED && (
                <>
                  <SelectControl
                    name="crop_generation_id"
                    label={t('selection.type.clan')}
                    options={cropGenerations}
                    control={form.control}
                    placeholder={t('choose.data')}
                    className="col-span-4"
                  />
                  <SelectControl
                    name="storage_type"
                    label={t('storage.type')}
                    options={storageTypes}
                    control={form.control}
                    placeholder={t('choose.data')}
                    className="col-span-2"
                  />
                </>
              )}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline"> {t('cancel')}</Button>
              </DialogClose>
              <Button
                type="submit"
                isLoading={
                  isCotton
                    ? isManageContractLoading
                    : isManageGrainContractLoading
                }
                disabled={!form.formState.isValid}
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};
